import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import axios from "axios";
import AddDeliveryManSetup from "../AddDeliveryManSetup/AddDeliveryManSetup.tsx";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";

function DeliveryManSetupPage() {
  const [open, setOpen] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const apiAddress = process.env.REACT_APP_SECRET || "https://localhost:7175";

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getCourierId = () => {
    const token = localStorage.getItem("gotasteadmintoken");
    if (!token) return null;

    try {
      const decoded = jwt_decode(token);
      return decoded?.courierId;
    } catch (err) {
      console.error("Token decode error:", err);
      return null;
    }
  };

  const formatDate = (isoString) => {
    if (!isoString || isoString.includes("0001-01-01")) return "—";

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getRelativeTime = (isoString) => {
    if (!isoString || isoString.includes("0001-01-01")) return "";

    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)} hr ago`;

    return formatDate(isoString);
  };

  const fetchInvitations = async () => {
    const courierId = getCourierId();

    if (!courierId) {
      setLoading(false);
      setError("Invalid token. Please login again.");
      setTimeout(() => window.location.href = "/", 800);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${apiAddress}/api/DeliveryMan/GetDeliveryManInvitations?Id=${courierId}`,
      );

      const formatted = response.data.map((item, index) => ({
        id: index + 1,
        invitationId: item.invitationId,
        email: item.email,
        contactNumber: item.contactNumber,
        status: item.status === true ? "Pending" : "Accepted",
        statusColor: item.status === true ? "warning" : "success",
        sentAt: item.sentAt,
        sentDisplay: formatDate(item.sentAt),
        acceptedAt: item.acceptedAt,
        acceptedDisplay: item.acceptedAt?.includes("0001-01-01")
          ? "Not yet"
          : formatDate(item.acceptedAt),
      }));

      setInvitations(formatted);
    } catch (err) {
      console.error("Failed to load invitations:", err);
      setError("Failed to load invitation list. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); 
  };
  const handleResendInvitation = async (
    invitationId: string | number,
    email: string,
  ) => {
    try {
      const res = await axios.post(
        `${apiAddress}/api/DeliveryMan/ResendInvitations?Id=${invitationId}`,
      ); 
      toast.success(res.data);
      console.log("zxzxzx", invitationId, email, invitations);
      fetchInvitations(); 
    } catch (err) {
      console.error(err);
      alert("Failed to resend invitation");
    }
  };


  useEffect(() => {
    fetchInvitations();
  }, []);
  return (
    <div style={{ padding: "24px" }}>
      {/* Header + Add button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h5" component="h1" fontWeight={600}>
          Delivery Man Invitations
        </Typography>

        <Button
          variant="contained"
          color="primary"
          size="medium"
          sx={{
            borderRadius: "50%",
            minWidth: "52px",
            height: "52px",
            fontSize: "1.5rem",
          }}
          onClick={handleOpen}
        >
          +
        </Button>
      </Box>

      {/* Dialog for adding new invitation */}
      <AddDeliveryManSetup
        open={open}
        onHandleAddItem={handleClose}
        title="Invite Delivery Man"
        fetchInvitations={fetchInvitations}
      />

      {loading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" mt={2}>
            Loading invitations...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Box>
      ) : invitations.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            bgcolor: "#f9f9f9",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No invitations have been sent yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Click the + button to invite a new delivery partner.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Contact Number
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Action
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Sent
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Accepted
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? invitations.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  )
                : invitations
              ).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.email}</TableCell>
                  <TableCell align="center">{row.contactNumber}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.status}
                      color={row.statusColor}
                      size="small"
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {row.status?.toLowerCase() === "pending" && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() =>
                          handleResendInvitation(row.invitationId, row.email)
                        }
                        sx={{ minWidth: 80 }}
                      >
                        Resend
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={formatDate(row.sentAt)}
                      arrow
                      placement="top"
                    >
                      <span>{row.sentDisplay}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={formatDate(row.acceptedAt)}
                      arrow
                      placement="top"
                    >
                      <span
                        style={{
                          color:
                            row.acceptedDisplay === "Not yet"
                              ? "#757575"
                              : "#2e7d32",
                          fontWeight:
                            row.acceptedDisplay !== "Not yet" ? 500 : 400,
                        }}
                      >
                        {row.acceptedDisplay}
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 25, { label: "All", value: -1 }]}
            component="div"
            count={invitations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </TableContainer>
      )}
    </div>
  );
}

export default DeliveryManSetupPage;
